import { Form, Formik, Field } from 'formik';
import { object, string } from "yup";
import { usePackAssets, usePack } from "lib/hooks";
import {
  Box,
  FormControl,
  FormLabel,
  VStack,
  Textarea,
  ButtonGroup,
  IconButton,
  FormErrorMessage, useDisclosure
} from '@chakra-ui/react';
import { CheckIcon, DeleteIcon } from "@chakra-ui/icons";
import { useMemo, useState, FC } from "react";
import { useDndAsset } from "domains/drag-and-drop";
import { DeleteAlertDialog } from "components";
import { usePackContext } from 'domains/pack';
import { useCreateAsset } from 'lib/hooks/useCreateAsset';

const validationSchema = object({
  value: string().required("HTML is required"),
});

export const HtmlPrompt: FC<{ isEditable: boolean }> = ({ isEditable }) => {
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { data } = usePackAssets(pack);
  const { asset, path } = useDndAsset();
  const deleteDialog = useDisclosure();
  const [ isPending, setIsPending ] = useState<boolean>(false);
  const { html } = useCreateAsset();

  const onSubmit = async ({ value } : { value: string; }): Promise<void> => {
    setIsPending(true);
    await html(value, asset.id);
    setIsPending(false);
  }

  // TODO: Put in utility and test
  const isInColumn: boolean = useMemo(() => {
    const indexes = path.split('-');
    if (indexes.length < 2) return false;

    const rowChildren = data[indexes[0]].children[indexes[1]]?.children;
    if (!rowChildren) return false;

    return rowChildren.length > 1;
  }, [ data, path ]);

  return (
    <Box
      minH={"100px"}
      px={5}
      py={isInColumn ? 5 : 7}
    >
      <Formik initialValues={{ value: asset.data.value }} validationSchema={validationSchema} onSubmit={onSubmit}>
        <Form>
          <VStack spacing={4} width={"100%"} align={["start", "end"]} mt={5}>
            <Field name="value">
              {({ field, form: { errors, touched } }) => (
                <FormControl isInvalid={errors.value && touched.value} mb={errors.value && touched.value ? [5, 0] : 0} isRequired>
                  <FormLabel htmlFor="value" size={"sm"}>Enter your html/embed code</FormLabel>
                  <Textarea
                    {...field}
                    size={"sm"}
                    borderRadius={"4px"}
                    placeholder={"Add your code"}
                    minHeight={"100px"}
                    _hover={{
                      borderColor: "brand.500"
                    }}
                    _active={{
                      borderColor: "brand.500"
                    }}
                    _focus={{
                      borderColor: "brand.500"
                    }}
                  />
                  <FormErrorMessage position={"absolute"}>{errors.value}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <ButtonGroup width={["100%", "auto"]}>
              {isEditable ? (
                <IconButton
                  width={["50%", "40px"]}
                  icon={<DeleteIcon/>}
                  aria-label={"Delete"}
                  borderColor={"red"}
                  onClick={deleteDialog.onOpen}
                />) : null
              }
              <IconButton
                width={["50%", "40px"]}
                icon={<CheckIcon />}
                aria-label={"Save"}
                variant={"primary"}
                isLoading={isPending}
                type={"submit"}
              />
            </ButtonGroup>
          </VStack>
        </Form>
      </Formik>
      <DeleteAlertDialog
        title={"Are you sure you want to delete this HTML dropzone?"}
        content={"This action cannot be undone."}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        asset={asset}
      />
    </Box>
  )
}